<template>
  <div class="content">
    <div class="container-fluid">
      <div class="row">
        <b-modal
          id="modal-add-config"
          hide-footer
          title="Add Meter Configuration"
          content-class="shadow"
        >
          <template #modal-header="{ close }">
            <h5>Add Meter Configuration</h5>
            <p class="h5 mb-2">
              <b-icon
                icon="x-square"
                class="close-icon"
                @click="close(); clearData();"
              ></b-icon>
            </p>
          </template>
          <validation-observer ref="observer" v-slot="{ handleSubmit }">
            <b-form
              class="text-left"
              @submit.stop.prevent="handleSubmit(submitAddForm)"
            >
              <b-row no-gutters>
                <b-col md="12">
                  <Vselect
                    :modelValue="formData.buildingId"
                    @update:modelValue="(val) => (formData.buildingId = val)"
                    :options="buildingOptions"
                    :rules="{ required: true }"
                    label="Building"
                    validatorName="Building"
                  />
                </b-col>
                <b-col md="12">
                  <Vselect
                    :modelValue="formData.type"
                    @update:modelValue="(val) => { formData.type = val; updateUnit(val); }"
                    :options="meterTypeOptions"
                    :rules="{ required: true }"
                    label="Meter Type"
                    validatorName="Meter Type"
                  />
                </b-col>
                <b-col md="12">
                  <Vselect
                    :modelValue="formData.meteringMethod"
                    @update:modelValue="(val) => { formData.meteringMethod = val; updateFormFields(val); }"
                    :options="meteringMethodOptions"
                    :rules="{ required: true }"
                    label="Metering Method"
                    validatorName="Metering Method"
                  />
                </b-col>
                <b-col md="12">
                  <Vinput
                    :modelValue="formData.unit"
                    @update:modelValue="(val) => (formData.unit = val)"
                    :rules="{ required: true }"
                    label="Unit"
                    validatorName="Unit"
                    :disabled="true"
                  />
                </b-col>
                
                <!-- MULTIPLIER Fields -->
                <template v-if="formData.meteringMethod === 'MULTIPLIER'">
                  <b-col md="12">
                    <VinputNumber
                      :modelValue="formData.rate.multiplier"
                      @update:modelValue="(val) => (formData.rate.multiplier = val)"
                      :rules="{ required: true }"
                      label="Rate Multiplier"
                      validatorName="Rate Multiplier"
                      :step=".01"
                    />
                  </b-col>
                  <b-col md="12">
                    <VinputNumber
                      :modelValue="formData.rate.fixedCharge"
                      @update:modelValue="(val) => (formData.rate.fixedCharge = val)"
                      :rules="{ required: true }"
                      label="Fixed Charge"
                      validatorName="Fixed Charge"
                      :step=".01"
                    />
                  </b-col>
                </template>
                
                <!-- BLOCK Fields -->
                <template v-if="formData.meteringMethod === 'BLOCK'">
                  <b-col md="12">
                    <label>Meter Blocks</label>
                    <div v-for="(block, index) in formData.blocks" :key="index" class="border p-2 mb-2">
                      <b-row>
                        <b-col md="6">
                          <VinputNumber
                            :modelValue="block.blockOrder"
                            @update:modelValue="(val) => (block.blockOrder = val)"
                            :rules="{ required: true }"
                            label="Block Order"
                            :step="1"
                          />
                        </b-col>
                        <b-col md="6">
                          <Vinput
                            :modelValue="block.blockName"
                            @update:modelValue="(val) => (block.blockName = val)"
                            :rules="{ required: true }"
                            label="Block Name"
                          />
                        </b-col>
                        <b-col md="6">
                          <VinputNumber
                            :modelValue="block.startRange"
                            @update:modelValue="(val) => (block.startRange = val)"
                            :rules="{ required: true }"
                            label="Start Range"
                            :step=".01"
                          />
                        </b-col>
                        <b-col md="6">
                          <VinputNumber
                            :modelValue="block.endRange"
                            @update:modelValue="(val) => (block.endRange = val)"
                            label="End Range"
                            :step=".01"
                            :disabled="block.unlimited"
                          />
                        </b-col>
                        <b-col md="6">
                          <VinputNumber
                            :modelValue="block.pricePerUnit"
                            @update:modelValue="(val) => (block.pricePerUnit = val)"
                            :rules="{ required: true }"
                            label="Price Per Unit"
                            :step=".01"
                          />
                        </b-col>
                        <b-col md="6">
                          <b-form-checkbox
                            v-model="block.unlimited"
                            @change="(val) => { if(val) block.endRange = null; }"
                          >
                            Unlimited
                          </b-form-checkbox>
                        </b-col>
                        <b-col md="12">
                          <Vinput
                            :modelValue="block.blockDescription"
                            @update:modelValue="(val) => (block.blockDescription = val)"
                            label="Block Description"
                          />
                        </b-col>
                        <b-col md="12" class="text-right">
                          <b-button variant="danger" size="sm" @click="removeBlock(index)" v-if="formData.blocks.length > 1">
                            Remove Block
                          </b-button>
                        </b-col>
                      </b-row>
                    </div>
                    <b-button variant="success" size="sm" @click="addBlock">
                      Add Block
                    </b-button>
                  </b-col>
                </template>
                <b-col md="12">
                  <Vinput
                    inputType="text"
                    :modelValue="formData.description"
                    @update:modelValue="(val) => (formData.description = val)"
                    label="Description"
                  />
                </b-col>
                <b-col class="mt-2">
                  <b-button type="submit" variant="primary">Submit</b-button>
                </b-col>
              </b-row>
            </b-form>
          </validation-observer>
        </b-modal>
      </div>

      <div class="row">
        <b-modal
          id="modal-detail"
          hide-footer
          title="Details"
          content-class="shadow"
          size="xl"
        >
          <template #modal-header="{ close }">
            <h5>Detail</h5>
            <p class="h5 mb-2">
              <b-icon
                icon="x-square"
                class="close-icon"
                @click="close();"
              ></b-icon>
            </p>
          </template>
          <b-row>
            <b-col md="12">
              <b-table-simple
                :small="true"
                sticky-header
                style="max-height: 395px;"
                striped
                responsive="sm"
                hover
                bordered
              >
                <b-thead>
                  <b-tr>
                    <b-th colspan="7" style="font-weight: 700;text-align: center !important;">Meter Configuration Information</b-th>
                  </b-tr>
                  <b-tr style="font-weight: 700;text-align: center !important;">
                    <b-th style="width: 15%">Building</b-th>
                    <b-th style="width: 10%">Type</b-th>
                    <b-th style="width: 10%">Method</b-th>
                    <b-th style="width: 10%">Unit</b-th>
                    <b-th style="width: 20%">Description</b-th>
                  </b-tr>
                </b-thead>
                <b-tbody>
                  <b-tr>
                    <b-td>{{ detailData.buildingName }}</b-td>
                    <b-td>{{ detailData.type }}</b-td>
                    <b-td>{{ detailData.meteringMethod }}</b-td>
                    <b-td>{{ detailData.unit }}</b-td>
                    <b-td>{{ detailData.description }}</b-td>
                  </b-tr>
                </b-tbody>
              </b-table-simple>
            </b-col>
          </b-row>
          <b-row>
            <b-col md="12" v-if="detailData.meteringMethod === 'MULTIPLIER' && detailData.rates && detailData.rates.length > 0">
              <b-table-simple
                :small="true"
                sticky-header
                style="max-height: 395px;"
                striped
                responsive="sm"
                hover
                bordered
              >
                <b-thead>
                  <b-tr>
                    <b-th colspan="4" style="font-weight: 700;text-align: center !important;">Rate History</b-th>
                  </b-tr>
                  <b-tr>
                    <b-th>Multiplier</b-th>
                    <b-th>Min Charge</b-th>
                    <b-th>Fixed Charge</b-th>
                    <b-th>Created Date</b-th>
                  </b-tr>
                </b-thead>
                <b-tbody>
                  <b-tr v-for="rate in detailData.rates" :key="rate.rateId">
                    <b-td>{{ rate.multiplier }}</b-td>
                    <b-td>{{ rate.minimumCharge }}</b-td>
                    <b-td>{{ rate.fixedCharge }}</b-td>
                    <b-td>{{ rate.createdDate }}</b-td>
                  </b-tr>
                </b-tbody>
              </b-table-simple>
            </b-col>
          </b-row>
          <b-row>
            <b-col md="12" v-if="detailData.meteringMethod === 'BLOCK' && detailData.blocks && detailData.blocks.length > 0">
              <b-table-simple
                :small="true"
                sticky-header
                style="max-height: 395px;"
                striped
                responsive="sm"
                hover
                bordered
              >
                <b-thead>
                  <b-tr>
                    <b-th colspan="7" style="font-weight: 700;text-align: center !important;">Block Configuration</b-th>
                  </b-tr>
                  <b-tr>
                    <b-th>Order</b-th>
                    <b-th>Block Name</b-th>
                    <b-th>Start Range</b-th>
                    <b-th>End Range</b-th>
                    <b-th>Price/Unit</b-th>
                    <b-th>Unlimited</b-th>
                    <b-th>Description</b-th>
                  </b-tr>
                </b-thead>
                <b-tbody>
                  <b-tr v-for="block in detailData.blocks" :key="block.blockId">
                    <b-td>{{ block.blockOrder }}</b-td>
                    <b-td>{{ block.blockName }}</b-td>
                    <b-td>{{ block.startRange }}</b-td>
                    <b-td>{{ block.unlimited ? 'N/A' : block.endRange }}</b-td>
                    <b-td>{{ block.pricePerUnit }}</b-td>
                    <b-td>{{ block.unlimited ? 'Yes' : 'No' }}</b-td>
                    <b-td>{{ block.blockDescription }}</b-td>
                  </b-tr>
                </b-tbody>
              </b-table-simple>
            </b-col>
          </b-row>
        </b-modal>
      </div>

      <div class="row">
        <b-modal
          id="modal-edit-config"
          hide-footer
          title="Edit Meter Configuration"
          content-class="shadow"
        >
          <template #modal-header="{ close }">
            <h5>Edit Meter Configuration</h5>
            <p class="h5 mb-2">
              <b-icon
                icon="x-square"
                class="close-icon"
                @click="close(); clearData();"
              ></b-icon>
            </p>
          </template>
          <validation-observer ref="observer" v-slot="{ handleSubmit }">
            <b-form
              class="text-left"
              @submit.stop.prevent="handleSubmit(submitEditForm)"
            >
              <b-row no-gutters>
                <b-col md="12">
                  <Vselect
                    :modelValue="editData.buildingId"
                    @update:modelValue="(val) => (editData.buildingId = val)"
                    :options="buildingOptions"
                    :rules="{ required: true }"
                    label="Building"
                    validatorName="Building"
                  />
                </b-col>
                <b-col md="12">
                  <Vselect
                    :modelValue="editData.type"
                    @update:modelValue="(val) => { editData.type = val; updateEditUnit(val); }"
                    :options="meterTypeOptions"
                    :rules="{ required: true }"
                    label="Meter Type"
                    validatorName="Meter Type"
                  />
                </b-col>
                <b-col md="12">
                  <Vselect
                    :modelValue="editData.meteringMethod"
                    @update:modelValue="(val) => { editData.meteringMethod = val; updateEditFormFields(val); }"
                    :options="meteringMethodOptions"
                    :rules="{ required: true }"
                    label="Metering Method"
                    validatorName="Metering Method"
                  />
                </b-col>
                <b-col md="12">
                  <Vinput
                    :modelValue="editData.unit"
                    @update:modelValue="(val) => (editData.unit = val)"
                    :rules="{ required: true }"
                    label="Unit"
                    validatorName="Unit"
                    :disabled="true"
                  />
                </b-col>
                
                <!-- MULTIPLIER Fields -->
                <template v-if="editData.meteringMethod === 'MULTIPLIER'">
                  <b-col md="12">
                    <VinputNumber
                      :modelValue="editData.rate.multiplier"
                      @update:modelValue="(val) => (editData.rate.multiplier = val)"
                      :rules="{ required: true }"
                      label="Rate Multiplier"
                      validatorName="Rate Multiplier"
                      :step=".01"
                    />
                  </b-col>
                  <b-col md="12">
                    <VinputNumber
                      :modelValue="editData.rate.fixedCharge"
                      @update:modelValue="(val) => (editData.rate.fixedCharge = val)"
                      :rules="{ required: true }"
                      label="Fixed Charge"
                      validatorName="Fixed Charge"
                      :step=".01"
                    />
                  </b-col>
                </template>
                
                <!-- BLOCK Fields -->
                <template v-if="editData.meteringMethod === 'BLOCK'">
                  <b-col md="12">
                    <label>Meter Blocks</label>
                    <div v-for="(block, index) in editData.blocks" :key="index" class="border p-2 mb-2">
                      <b-row>
                        <b-col md="6">
                          <VinputNumber
                            :modelValue="block.blockOrder"
                            @update:modelValue="(val) => (block.blockOrder = val)"
                            :rules="{ required: true }"
                            label="Block Order"
                            :step="1"
                          />
                        </b-col>
                        <b-col md="6">
                          <Vinput
                            :modelValue="block.blockName"
                            @update:modelValue="(val) => (block.blockName = val)"
                            :rules="{ required: true }"
                            label="Block Name"
                          />
                        </b-col>
                        <b-col md="6">
                          <VinputNumber
                            :modelValue="block.startRange"
                            @update:modelValue="(val) => (block.startRange = val)"
                            :rules="{ required: true }"
                            label="Start Range"
                            :step=".01"
                          />
                        </b-col>
                        <b-col md="6">
                          <VinputNumber
                            :modelValue="block.endRange"
                            @update:modelValue="(val) => (block.endRange = val)"
                            label="End Range"
                            :step=".01"
                            :disabled="block.unlimited"
                          />
                        </b-col>
                        <b-col md="6">
                          <VinputNumber
                            :modelValue="block.pricePerUnit"
                            @update:modelValue="(val) => (block.pricePerUnit = val)"
                            :rules="{ required: true }"
                            label="Price Per Unit"
                            :step=".01"
                          />
                        </b-col>
                        <b-col md="6">
                          <b-form-checkbox
                            v-model="block.unlimited"
                            @change="(val) => { if(val) block.endRange = null; }"
                          >
                            Unlimited
                          </b-form-checkbox>
                        </b-col>
                        <b-col md="12">
                          <Vinput
                            :modelValue="block.blockDescription"
                            @update:modelValue="(val) => (block.blockDescription = val)"
                            label="Block Description"
                          />
                        </b-col>
                        <b-col md="12" class="text-right">
                          <b-button variant="danger" size="sm" @click="removeEditBlock(index)" v-if="editData.blocks.length > 1">
                            Remove Block
                          </b-button>
                        </b-col>
                      </b-row>
                    </div>
                    <b-button variant="success" size="sm" @click="addEditBlock">
                      Add Block
                    </b-button>
                  </b-col>
                </template>
                <b-col md="12">
                  <Vinput
                    inputType="text"
                    :modelValue="editData.description"
                    @update:modelValue="(val) => (editData.description = val)"
                    label="Description"
                  />
                </b-col>
                <b-col class="mt-2">
                  <b-button type="submit" variant="primary">Update</b-button>
                </b-col>
              </b-row>
            </b-form>
          </validation-observer>
        </b-modal>
      </div>

      <div class="row">
        <div class="col-12">
          <card>
            <template slot="header">
              <b-row>
                <b-col lg="12">
                  <b-button @click="openAddModal" class="btn-outline-primary">
                    <b-icon icon="plus"></b-icon> New Meter Config
                  </b-button>
                </b-col>
              </b-row>
            </template>
            <b-table-simple
              :small="true"
              sticky-header
              style="max-height: 395px;"
              striped
              responsive="sm"
              hover
            >
              <b-thead>
                <b-tr>
                  <b-th>No</b-th>
                  <b-th>Building</b-th>
                  <b-th>Type</b-th>
                  <b-th>Method</b-th>
                  <b-th>Unit</b-th>
                  <b-th>Rate/Price</b-th>
                  <b-th>Actions</b-th>
                </b-tr>
              </b-thead>
              <b-tbody v-if="spinner">
                <b-tr>
                  <b-td colspan="6" class="text-center">
                    <b-spinner variant="warning"></b-spinner>
                  </b-td>
                </b-tr>
              </b-tbody>
              <b-tbody v-else-if="items.length === 0">
                <b-tr>
                  <b-td colspan="6" class="text-center">No data found</b-td>
                </b-tr>
              </b-tbody>
              <b-tbody v-else>
                <b-tr v-for="(item, index) in items" :key="item.id">
                  <b-td>{{ index + 1 }}</b-td>
                  <b-td>{{ item.buildingName }}</b-td>
                  <b-td>{{ item.type }}</b-td>
                  <b-td>{{ item.meteringMethod }}</b-td>
                  <b-td>{{ item.unit }}</b-td>
                  <b-td>{{ getDisplayValue(item) }}</b-td>
                  <b-td>
                    <b-icon icon="eye-fill" class="btn-icon mr-2" @click="openDetailModal(item)"></b-icon>
                    <b-icon icon="pencil-square" class="btn-icon" @click="openEditModal(item)"></b-icon>
                  </b-td>
                </b-tr>
              </b-tbody>
            </b-table-simple>
          </card>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import Card from 'src/components/Cards/Card.vue'
import Vinput from 'src/components/Inputs/Vinput.vue'
import VinputNumber from 'src/components/Inputs/VinputNumber.vue'
import Vselect from 'src/components/Inputs/Vselect.vue'

export default {
  components: { Card, Vinput, VinputNumber, Vselect },
  data() {
    return {
      spinner: false,
      items: [],
      buildingOptions: [],
      meteringMethodOptions: [
        { value: 'MULTIPLIER', text: 'MULTIPLIER' },
        { value: 'BLOCK', text: 'BLOCK' }
      ],
      meterTypeOptions: [
        { value: 'ELECTRIC', text: 'ELECTRIC' },
        { value: 'WATER', text: 'WATER' }
      ],
      detailData: {},
      formData: {
        buildingId: '',
        type: '',
        meteringMethod: '',
        unit: '',
        description: '',
        registeredBy: parseInt(sessionStorage.getItem('userID')),
        rate: {
          multiplier: null,
          minimumCharge: 0.0,
          fixedCharge: 0.0
        },
        blocks: [{
          blockOrder: 1,
          startRange: 0,
          endRange: null,
          pricePerUnit: 0,
          blockName: '',
          blockDescription: '',
          unlimited: false
        }]
      },
      editData: {
        configId: null,
        buildingId: '',
        type: '',
        meteringMethod: '',
        unit: '',
        description: '',
        registeredBy: parseInt(sessionStorage.getItem('userID')),
        rate: {
          rateId: null,
          multiplier: null,
          minimumCharge: 0.0,
          fixedCharge: 0.0
        },
        blocks: []
      }
    }
  },

  mounted() {
    this.loadBuildings()
    this.loadConfigs()
  },
  methods: {
    loadBuildings() {
      this.$store.dispatch('getBuildingsByOwnerID', { userId: sessionStorage.getItem('ID') })
        .then(response => {
          this.buildingOptions = response.data.map(b => ({
            value: b.buildingId,
            text: b.buildingName
          }))
        })
        .catch(error => {
          this.$notifications.notify({
            message: 'Failed to load buildings',
            type: 'danger'
          })
        })
    },
    loadConfigs() {
      this.spinner = true
      this.$store.dispatch('getReadingConfig', { userId: parseInt(sessionStorage.getItem('ID')) })
        .then(response => {
          this.items = response.data || []
        })
        .catch(error => {
          this.items = []
        })
        .finally(() => {
          this.spinner = false
        })
    },
    openAddModal() {
      this.$bvModal.show('modal-add-config')
    },
    openDetailModal(item) {
      this.detailData = item
      this.$bvModal.show('modal-detail')
    },
    openEditModal(item) {
      const latestRate = this.getLatestRate(item.rates)
      this.editData = {
        configId: item.id,
        buildingId: item.buildingId,
        type: item.type,
        meteringMethod: item.meteringMethod,
        unit: item.unit,
        description: item.description,
        registeredBy: parseInt(sessionStorage.getItem('userID')),
        rate: {
          rateId: latestRate.rateId || null,
          multiplier: latestRate.multiplier || 0,
          minimumCharge: latestRate.minimumCharge || 0,
          fixedCharge: latestRate.fixedCharge || 0
        },
        blocks: item.blocks ? [...item.blocks] : []
      }
      this.$bvModal.show('modal-edit-config')
    },
    getLatestRate(rates) {
      if (!rates || rates.length === 0) return { rateId: null, multiplier: 0, minimumCharge: 0, fixedCharge: 0 }
      return rates[rates.length - 1]
    },
    getDisplayValue(item) {
      if (item.meteringMethod === 'MULTIPLIER' && item.rates && item.rates.length > 0) {
        const rate = item.rates[item.rates.length - 1]
        return `${rate.multiplier} (Min: ${rate.minimumCharge})`
      } else if (item.meteringMethod === 'BLOCK' && item.blocks && item.blocks.length > 0) {
        return `${item.blocks.length} blocks`
      }
      return 'N/A'
    },
    submitAddForm() {
      this.$bvModal.msgBoxConfirm('Are you sure you want to create this meter configuration?', {
        title: 'Confirm Creation',
        size: 'sm',
        buttonSize: 'sm',
        okVariant: 'success',
        okTitle: 'Yes',
        cancelTitle: 'No',
        footerClass: 'p-2',
        hideHeaderClose: false,
        centered: true
      })
      .then(value => {
        if (value) {
          this.$store.dispatch('createReadingConfig', this.formData)
            .then(response => {
              this.$notifications.notify({
                message: 'Meter config successfully created',
                type: 'success'
              })
              this.$bvModal.hide('modal-add-config')
              this.clearData()
              this.loadConfigs()
            })
            .catch(error => {
              this.$notifications.notify({
                message: (error.response && error.response.data) || 'Failed to create config',
                type: 'danger'
              })
            })
        }
      })
    },
    submitEditForm() {
      this.$bvModal.msgBoxConfirm('Are you sure you want to update this meter configuration?', {
        title: 'Confirm Update',
        size: 'sm',
        buttonSize: 'sm',
        okVariant: 'success',
        okTitle: 'Yes',
        cancelTitle: 'No',
        footerClass: 'p-2',
        hideHeaderClose: false,
        centered: true
      })
      .then(value => {
        if (value) {
          this.$store.dispatch('updateReadingConfig', this.editData)
            .then(response => {
              this.$notifications.notify({
                message: 'Meter config successfully updated',
                type: 'success'
              })
              this.$bvModal.hide('modal-edit-config')
              this.clearData()
              this.loadConfigs()
            })
            .catch(error => {
              this.$notifications.notify({
                message: (error.response && error.response.data) || 'Failed to update config',
                type: 'danger'
              })
            })
        }
      })
    },
    updateFormFields(meteringMethod) {
      if (meteringMethod === 'MULTIPLIER') {
        this.formData.blocks = []
      } else if (meteringMethod === 'BLOCK') {
        this.formData.rate = {
          multiplier: null,
          minimumCharge: 0.0,
          fixedCharge: 0.0
        }
        if (this.formData.blocks.length === 0) {
          this.addBlock()
        }
      }
    },
    addBlock() {
      this.formData.blocks.push({
        blockOrder: this.formData.blocks.length + 1,
        startRange: 0,
        endRange: null,
        pricePerUnit: 0,
        blockName: '',
        blockDescription: '',
        unlimited: false
      })
    },
    removeBlock(index) {
      this.formData.blocks.splice(index, 1)
      // Reorder blocks
      this.formData.blocks.forEach((block, i) => {
        block.blockOrder = i + 1
      })
    },
    updateUnit(meterType) {
      if (meterType === 'ELECTRIC') {
        this.formData.unit = 'kWh'
      } else if (meterType === 'WATER') {
        this.formData.unit = 'm3'
      }
    },
    updateEditFormFields(meteringMethod) {
      if (meteringMethod === 'MULTIPLIER') {
        this.editData.blocks = []
      } else if (meteringMethod === 'BLOCK') {
        this.editData.rate = {
          rateId: null,
          multiplier: null,
          minimumCharge: 0.0,
          fixedCharge: 0.0
        }
        if (this.editData.blocks.length === 0) {
          this.addEditBlock()
        }
      }
    },
    addEditBlock() {
      this.editData.blocks.push({
        blockOrder: this.editData.blocks.length + 1,
        startRange: 0,
        endRange: null,
        pricePerUnit: 0,
        blockName: '',
        blockDescription: '',
        unlimited: false
      })
    },
    removeEditBlock(index) {
      this.editData.blocks.splice(index, 1)
      this.editData.blocks.forEach((block, i) => {
        block.blockOrder = i + 1
      })
    },
    updateEditUnit(meterType) {
      if (meterType === 'ELECTRIC') {
        this.editData.unit = 'kWh'
      } else if (meterType === 'WATER') {
        this.editData.unit = 'm3'
      }
    },
    clearData() {
      this.formData = {
        buildingId: '',
        type: '',
        meteringMethod: '',
        unit: '',
        description: '',
        registeredBy: parseInt(sessionStorage.getItem('userID')),
        rate: {
          multiplier: null,
          minimumCharge: 0.0,
          fixedCharge: 0.0
        },
        blocks: [{
          blockOrder: 1,
          startRange: 0,
          endRange: null,
          pricePerUnit: 0,
          blockName: '',
          blockDescription: '',
          unlimited: false
        }]
      }
      this.editData = {
        configId: null,
        buildingId: '',
        type: '',
        meteringMethod: '',
        unit: '',
        description: '',
        registeredBy: parseInt(sessionStorage.getItem('userID')),
        rate: {
          rateId: null,
          multiplier: null,
          minimumCharge: 0.0,
          fixedCharge: 0.0
        },
        blocks: []
      }
    }
  }
}
</script>

<style scoped>
.btn-icon {
  font-size: 24px;
  cursor: pointer;
  color: rgb(37, 91, 48);
}
.btn-icon:hover {
  color: rgba(240, 206, 13, 0.937);
}
.close-icon {
  cursor: pointer;
  font-size: 24px;
}
</style>
